import type { LucideIcon } from "lucide-react"
import type { ReactNode } from "react"
import { cn } from "@/primitives/utils"

/*
 * A note set into a page or a section: that a Project is archived, that a Slack rule is broken,
 * that someone else changed a title, that a section failed to load. The tone keeps each token's
 * meaning: a broken rule or a failed connection is `feed-error`, never a warning; an edit conflict
 * is a `warning`; archived or read-only is `neutral`; a section that failed is `destructive`; and
 * `info` is a fact worth knowing before acting.
 */

const tones = {
  neutral: { surface: "bg-muted ring-border", icon: "text-muted-foreground" },
  info: { surface: "bg-primary/8 ring-primary/20", icon: "text-brand-text" },
  warning: { surface: "bg-warning/8 ring-warning/25", icon: "text-warning" },
  "feed-error": { surface: "bg-feed-error/8 ring-feed-error/20", icon: "text-feed-error" },
  destructive: { surface: "bg-destructive/8 ring-destructive/20", icon: "text-destructive" },
} as const

/**
 * `children` is the note itself, under an optional one-line `title`; `action` is the one thing to
 * do about it, a small Button, kept at the end.
 */
export function Callout({
  tone,
  icon: Icon,
  title,
  children,
  action,
}: {
  tone: keyof typeof tones
  icon: LucideIcon
  title?: string
  children: ReactNode
  action?: ReactNode
}) {
  const { surface, icon } = tones[tone]
  return (
    <div
      data-slot="callout"
      className={cn(
        "flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-ui text-foreground ring-1 ring-inset",
        surface,
      )}
    >
      <Icon aria-hidden className={cn("mt-0.5 size-4 shrink-0", icon)} />
      <div className="min-w-0 flex-1 space-y-0.5">
        {title && <p className="font-medium">{title}</p>}
        <div className={cn(title && "text-muted-foreground")}>{children}</div>
      </div>
      {action && <div className="-my-0.5 ml-auto flex shrink-0 items-center gap-2">{action}</div>}
    </div>
  )
}
